import React, { useState, useEffect, useRef, useMemo } from 'react';
import AddressInput from '../ui/AddressInput';
import { Button, IconButton } from '../ui';

// ===== ROUTE POINTS EDITOR =====
// Mobile-friendly multi-stop route editor for the Estimator app. Mirrors
// the desktop MoveDetailsEditor route-points pattern but trades drag/drop
// reorder for chunky up/down arrow buttons that work cleanly on touch.
//
// Round-trip into the existing Supabase schema:
//   - first point  -> origin_address
//   - last point   -> destination_address
//   - middle stops -> additional_stops (JSON.stringified array of
//                     { address: '...' } objects, '' when no middle stops)
//
// This is exactly the shape MoveDetailsEditor reads on the desktop side,
// so a multi-stop route entered on the phone shows up correctly when the
// estimator opens the client back on the laptop.
//
// Props:
//   form           — full form/details object (must have origin_address,
//                    destination_address, additional_stops)
//   set            — single-key setter: set(key, value)
//   setBatch       — optional multi-key setter: setBatch({ k1, k2, k3 })
//                    used when available so we don't trigger 3 renders
//                    per route mutation
//   showDistances  — render the inline leg chips + total card. Default
//                    true; set false to skip Distance Matrix calls in
//                    very minimal contexts.
//   errors         — { origin_address: bool } so we can paint the origin
//                    row's input in the validation-error red ring used
//                    by the surrounding form.
//
// Maps API: relies on window.google.maps being available (loaded in
// index.html). Distance Matrix is part of the core library, no extra
// `libraries=` flag needed beyond what AddressInput already requires.
function RoutePointsEditor({ form, set, setBatch, showDistances = true, errors = {} }) {
    const parseStops = (val) => {
        if (!val) return [];
        try {
            const parsed = JSON.parse(val);
            if (Array.isArray(parsed)) return parsed;
        } catch (e) { /* not JSON, fall through */ }
        // Legacy single-stop fallback: an old client row could have a raw
        // string in additional_stops. Treat it as a single stop so we don't
        // lose the data when this editor first reads it.
        return val.trim() ? [{ address: val.trim() }] : [];
    };

    // Stable internal id counter so React keys stay unique across adds.
    const stopIdRef = useRef(0);

    const [routePoints, setRoutePoints] = useState(() => {
        const pts = [
            { id: 'origin', address: form.origin_address || '', pointType: 'origin' },
        ];
        const parsed = parseStops(form.additional_stops);
        parsed.forEach((s, i) => {
            stopIdRef.current = i + 1;
            pts.push({ id: `stop-${i}`, address: s.address || '', pointType: 'stop' });
        });
        pts.push({ id: 'dest', address: form.destination_address || '', pointType: 'destination' });
        return pts;
    });

    const addRouteStop = () => {
        const idx = stopIdRef.current++;
        // Insert just before the destination so new stops always land in
        // the middle of the route, never after the final destination.
        setRoutePoints(prev => {
            const before = prev.slice(0, prev.length - 1);
            const dest = prev[prev.length - 1];
            return [...before, { id: `stop-${idx}-${Date.now()}`, address: '', pointType: 'stop' }, dest];
        });
    };

    const updateRouteAddress = (id, address) =>
        setRoutePoints(prev => prev.map(p => p.id === id ? { ...p, address } : p));

    const removeRoutePoint = (id) =>
        setRoutePoints(prev => prev.filter(p => p.id !== id));

    const moveRoutePoint = (idx, direction) => {
        const newIdx = idx + direction;
        if (newIdx < 0 || newIdx >= routePoints.length) return;
        setRoutePoints(prev => {
            const u = [...prev];
            const [m] = u.splice(idx, 1);
            u.splice(newIdx, 0, m);
            return u;
        });
    };

    // Persist route changes back into the form. Always exactly one origin
    // (first), exactly one destination (last), and any middle items go to
    // additional_stops. Match MoveDetailsEditor exactly so round-trip with
    // desktop is byte-stable.
    useEffect(() => {
        if (routePoints.length === 0) return;
        const firstAddr = routePoints[0]?.address || '';
        const lastAddr = routePoints.length > 1 ? routePoints[routePoints.length - 1]?.address || '' : '';
        const middleStops = routePoints.length > 2 ? routePoints.slice(1, -1) : [];
        const stopsStr = middleStops.length > 0
            ? JSON.stringify(middleStops.map(p => ({ address: p.address })))
            : '';
        if (setBatch) {
            setBatch({ origin_address: firstAddr, destination_address: lastAddr, additional_stops: stopsStr });
        } else {
            set('origin_address', firstAddr);
            set('destination_address', lastAddr);
            set('additional_stops', stopsStr);
        }
    }, [routePoints]); // eslint-disable-line react-hooks/exhaustive-deps

    // ----- Distance Matrix (per-leg) -----
    const [legs, setLegs] = useState([]);
    const prevRouteKey = useRef('');

    const filledAddrs = useMemo(
        () => routePoints.filter(p => p.address?.trim()).map(p => p.address.trim()),
        [routePoints]
    );

    useEffect(() => {
        if (!showDistances) return;
        const routeKey = filledAddrs.join('|||');
        if (routeKey === prevRouteKey.current) return;
        if (filledAddrs.length < 2) {
            setLegs([]);
            prevRouteKey.current = routeKey;
            return;
        }
        const timer = setTimeout(async () => {
            if (!window.google?.maps) return;
            try {
                const service = new google.maps.DistanceMatrixService();
                const results = [];
                for (let i = 0; i < filledAddrs.length - 1; i++) {
                    const resp = await new Promise((resolve, reject) => {
                        service.getDistanceMatrix(
                            {
                                origins: [filledAddrs[i]],
                                destinations: [filledAddrs[i + 1]],
                                travelMode: 'DRIVING',
                                unitSystem: google.maps.UnitSystem.IMPERIAL,
                            },
                            (response, status) => {
                                if (status === 'OK') resolve(response);
                                else reject(new Error(status));
                            }
                        );
                    });
                    const el = resp?.rows?.[0]?.elements?.[0];
                    results.push(el?.status === 'OK'
                        ? { distance: el.distance.text, duration: el.duration.text, distanceM: el.distance.value, durationS: el.duration.value }
                        : null);
                }
                setLegs(results);
                prevRouteKey.current = routeKey;
            } catch (e) {
                console.warn('RoutePointsEditor distance lookup failed:', e);
                setLegs([]);
            }
        }, 800);
        return () => clearTimeout(timer);
    }, [filledAddrs, showDistances]);

    // Map filled-only legs back to between-row positions. A leg is shown
    // between routePoints[i] and routePoints[i+1] only when both are
    // filled — otherwise the index in `legs` (which is built from the
    // filtered list) wouldn't line up. Build a render-side lookup.
    const legByPair = useMemo(() => {
        const map = new Map();
        let legIdx = 0;
        for (let i = 0; i < routePoints.length - 1; i++) {
            const a = routePoints[i]?.address?.trim();
            const b = routePoints[i + 1]?.address?.trim();
            if (a && b) {
                if (legs[legIdx]) map.set(i, legs[legIdx]);
                legIdx++;
            }
        }
        return map;
    }, [routePoints, legs]);

    const tripTotals = useMemo(() => {
        const valid = legs.filter(l => l);
        if (!valid.length) return null;
        const totalDistM = valid.reduce((s, l) => s + (l.distanceM || 0), 0);
        const totalDurS = valid.reduce((s, l) => s + (l.durationS || 0), 0);
        const hours = Math.floor(totalDurS / 3600);
        const mins = Math.round((totalDurS % 3600) / 60);
        return {
            miles: (totalDistM / 1609.34).toFixed(1),
            time: hours > 0 ? `${hours}h ${mins}m` : `${mins} min`,
            legs: valid.length,
        };
    }, [legs]);

    // ----- Render -----
    const inputCls = 'w-full px-3 py-3 bg-white border border-slate-200 rounded-xl text-base focus:outline-none focus:ring-2 focus:ring-red-500/30';
    const errCls = 'border-red-400 ring-2 ring-red-200';

    const stopColors = [
        { bg: 'bg-emerald-50/60', border: 'border-emerald-200', dot: 'bg-emerald-600', label: 'text-emerald-700' },
        { bg: 'bg-amber-50/60',   border: 'border-amber-200',   dot: 'bg-amber-600',   label: 'text-amber-700' },
        { bg: 'bg-purple-50/60',  border: 'border-purple-200',  dot: 'bg-purple-600',  label: 'text-purple-700' },
        { bg: 'bg-cyan-50/60',    border: 'border-cyan-200',    dot: 'bg-cyan-600',    label: 'text-cyan-700' },
    ];

    return (
        <div className="space-y-1.5">
            {routePoints.map((point, idx) => {
                const num = idx + 1;
                let cardBg, cardBorder, dotBg, labelColor, labelText, isRequired, errorKey;
                if (point.pointType === 'origin') {
                    cardBg = 'bg-red-50/60';
                    cardBorder = 'border-red-200';
                    dotBg = 'bg-red-600';
                    labelColor = 'text-red-700';
                    labelText = 'Moving FROM';
                    isRequired = true;
                    errorKey = 'origin_address';
                } else if (point.pointType === 'destination') {
                    cardBg = 'bg-blue-50/60';
                    cardBorder = 'border-blue-200';
                    dotBg = 'bg-blue-600';
                    labelColor = 'text-blue-700';
                    labelText = 'Moving TO';
                    isRequired = false;
                    errorKey = null;
                } else {
                    // Color stops in a stable cycle keyed off the stop index
                    // (not idx, so reordering doesn't shuffle colors).
                    const stopIndex = routePoints.slice(0, idx).filter(p => p.pointType === 'stop').length;
                    const c = stopColors[stopIndex % stopColors.length];
                    cardBg = c.bg;
                    cardBorder = c.border;
                    dotBg = c.dot;
                    labelColor = c.label;
                    labelText = `Stop ${num - 1}`;
                    isRequired = false;
                    errorKey = null;
                }

                const canRemove = point.pointType === 'stop';
                const canMoveUp = idx > 0;
                const canMoveDown = idx < routePoints.length - 1;
                const hasErr = errorKey && errors[errorKey];
                const legAfter = legByPair.get(idx);
                const hasNext = idx < routePoints.length - 1;

                return (
                    <React.Fragment key={point.id}>
                        <div className={`${cardBg} border ${cardBorder} rounded-xl p-3`}>
                            <div className="flex items-center gap-2 mb-2">
                                <div className={`w-6 h-6 ${dotBg} rounded-full flex items-center justify-center flex-shrink-0`}>
                                    <span className="text-white text-[10px] font-bold">{num}</span>
                                </div>
                                <span className={`text-[11px] font-bold ${labelColor} uppercase tracking-wider`}>
                                    {labelText}
                                </span>
                                {isRequired && <span className="text-red-500 text-[11px]">*</span>}
                                <div className="flex-1"></div>
                                {/* One-tap Google navigation (Jaylen 2026-07-02) —
                                    estimators used to dig through the Street View
                                    popover to reach Maps. dir/?api=1 opens turn-by-
                                    turn to this address in the Google Maps app. */}
                                {point.address?.trim() && (
                                    <a
                                        href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(point.address)}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        aria-label={`Navigate to ${labelText} in Google Maps`}
                                        title="Navigate with Google Maps"
                                        className="w-8 h-8 rounded-lg bg-white/80 border border-slate-200 flex items-center justify-center active:scale-95 transition-transform"
                                    >
                                        {/* Google "G" mark */}
                                        <svg width="15" height="15" viewBox="0 0 24 24" aria-hidden="true">
                                            <path fill="#4285F4" d="M23.5 12.27c0-.85-.08-1.66-.22-2.45H12v4.64h6.46a5.53 5.53 0 0 1-2.4 3.62v3h3.88c2.27-2.1 3.56-5.17 3.56-8.81z"/>
                                            <path fill="#34A853" d="M12 24c3.24 0 5.96-1.07 7.94-2.91l-3.88-3c-1.08.72-2.45 1.15-4.06 1.15-3.13 0-5.78-2.11-6.72-4.96H1.27v3.1A12 12 0 0 0 12 24z"/>
                                            <path fill="#FBBC05" d="M5.28 14.28A7.2 7.2 0 0 1 4.9 12c0-.79.14-1.56.38-2.28v-3.1H1.27a12 12 0 0 0 0 10.76l4.01-3.1z"/>
                                            <path fill="#EA4335" d="M12 4.76c1.76 0 3.34.6 4.59 1.8l3.44-3.44A11.97 11.97 0 0 0 12 0 12 12 0 0 0 1.27 6.62l4.01 3.1C6.22 6.87 8.87 4.76 12 4.76z"/>
                                        </svg>
                                    </a>
                                )}
                                {/* Reorder controls — only show when there's
                                    actually room to reorder (3+ points). */}
                                {routePoints.length >= 3 && (
                                    <div className="flex items-center gap-1">
                                        <IconButton
                                            label="Move up"
                                            size="sm"
                                            onClick={() => moveRoutePoint(idx, -1)}
                                            disabled={!canMoveUp}
                                        >
                                            {'\u25B2'}
                                        </IconButton>
                                        <IconButton
                                            label="Move down"
                                            size="sm"
                                            onClick={() => moveRoutePoint(idx, 1)}
                                            disabled={!canMoveDown}
                                        >
                                            {'\u25BC'}
                                        </IconButton>
                                    </div>
                                )}
                                {canRemove && (
                                    <IconButton
                                        label={`Remove ${labelText}`}
                                        size="sm"
                                        tone="danger"
                                        onClick={() => removeRoutePoint(point.id)}
                                    >
                                        {'\u2715'}
                                    </IconButton>
                                )}
                            </div>
                            <AddressInput
                                value={point.address}
                                onChange={v => updateRouteAddress(point.id, v)}
                                placeholder={
                                    point.pointType === 'origin' ? 'Pickup address' :
                                    point.pointType === 'destination' ? 'Destination address' :
                                    `Stop ${num - 1} address`
                                }
                                className={`${inputCls} ${hasErr ? errCls : ''}`}
                            />
                        </div>

                        {/* Inline leg chip between rows. Only renders when
                            both endpoints are filled and we have a leg. */}
                        {showDistances && hasNext && legAfter && (
                            <div className="flex items-center justify-center py-0.5">
                                <div className="flex items-center gap-1.5 px-2 py-0.5 bg-slate-50 rounded-full border border-slate-200">
                                    <div className="w-px h-2 bg-slate-300"></div>
                                    <span className="text-[10px] font-medium text-slate-500">
                                        {legAfter.distance} <span className="text-slate-300">·</span> {legAfter.duration}
                                    </span>
                                    <div className="w-px h-2 bg-slate-300"></div>
                                </div>
                            </div>
                        )}
                    </React.Fragment>
                );
            })}

            {/* Add Stop button — full-width, dashed-border to match the
                "+ New Client" button style on the portal home. */}
            <Button
                variant="secondary"
                full
                className="mt-1"
                onClick={addRouteStop}
            >
                + Add Stop
            </Button>

            {/* Trip Total card — only shows once we have at least one
                successfully-resolved leg. */}
            {showDistances && tripTotals && (
                <div className="bg-slate-800 text-white rounded-xl p-3 flex items-center justify-between mt-1.5">
                    <div className="flex items-center gap-2 min-w-0">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-slate-300 flex-shrink-0">
                            <circle cx="12" cy="12" r="10"/>
                            <polyline points="12 6 12 12 16 14"/>
                        </svg>
                        <span className="text-[11px] font-bold uppercase tracking-wider">Trip Total</span>
                        <span className="text-[10px] text-slate-400 truncate">
                            ({tripTotals.legs} {tripTotals.legs === 1 ? 'leg' : 'legs'})
                        </span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-[13px] font-bold">{tripTotals.miles} mi</span>
                        <span className="text-slate-500">·</span>
                        <span className="text-[13px] font-semibold text-slate-300">{tripTotals.time}</span>
                    </div>
                </div>
            )}
        </div>
    );
}

export default RoutePointsEditor;
